import { Injectable } from '@angular/core';
import { RadioState } from '@app/core/states/radio.state';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { append, patch } from '@ngxs/store/operators';
import { PlanningUtilsService } from '@radioking/planning';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, mergeMap, take } from 'rxjs/operators';

import { DatesRange, Slot, SlotOccurrence } from '../models/planning.model';
import { PlanningService } from '../services/planning.service';

import { AppState } from '@app/core/states/app.state';
import moment from 'moment';
import {
  ClearPlanning,
  GetPlanningFailure,
  GetPlanningRequest,
  GetPlanningSuccess,
  PlanningCreateSlotFailure,
  PlanningCreateSlotRequest,
  PlanningCreateSlotSuccess,
  PlanningDeleteSlotFailure,
  PlanningDeleteSlotRequest,
  PlanningDeleteSlotSuccess,
  PlanningDropSlotRequest,
  PlanningEditSlotFailure,
  PlanningEditSlotRequest,
  PlanningEditSlotSuccess,
  PlanningMoveSlotFailure,
  PlanningMoveSlotRequest,
  PlanningMoveSlotSuccess,
  SetCurrentDay,
  SlotOverrideExisting,
} from './planning.actions';

export class PlanningStateModel {
  slots: Slot[];
  currentDatesRange: DatesRange;
  currentDay: moment.Moment | null;
}

@State<PlanningStateModel>({
  name: 'planning',
  defaults: {
    slots: [],
    currentDatesRange: null,
    currentDay: moment(),
  },
})
@Injectable()
export class PlanningState {
  constructor(
    private readonly store: Store,
    private readonly planningService: PlanningService,
  ) {}

  @Selector()
  static slots(state: PlanningStateModel): Slot[] {
    return state.slots;
  }

  @Selector()
  static allOccurrences(state: PlanningStateModel) {
    return (slotId: number): SlotOccurrence[] => {
      return state.slots
        .filter(s => (slotId ? s.id !== slotId : true))
        .map(s => s.occurrences)
        .reduce((acc, v) => acc.concat(v), [])
        .filter(occ => !!occ);
    };
  }

  @Selector()
  static datesRange(state: PlanningStateModel): DatesRange {
    return state.currentDatesRange;
  }

  @Selector()
  static currentDay(state: PlanningStateModel): moment.Moment {
    return state.currentDay;
  }

  @Action(GetPlanningRequest)
  getPlanning(ctx: StateContext<PlanningStateModel>, { range }: GetPlanningRequest) {
    // Range is managed by setCurrentDay action on mobile
    if (
      !this.store.selectSnapshot(AppState.onMobile) ||
      !ctx.getState().currentDatesRange
    ) {
      ctx.patchState({
        currentDatesRange: range,
      });
    }

    return combineLatest([this.getCurrentRadioId(), this.getCurrentRadioTimezone()]).pipe(
      mergeMap(([id, tz]) => {
        return this.planningService.getPlanning(id, tz, range.from, range.to);
      }),
      mergeMap(data => {
        ctx.setState(
          patch<PlanningStateModel>({
            slots: data,
          }),
        );
        return ctx.dispatch(new GetPlanningSuccess(data));
      }),
      catchError(err => {
        return ctx.dispatch(new GetPlanningFailure(err));
      }),
    );
  }

  @Action(PlanningCreateSlotRequest)
  createSlot(ctx: StateContext<PlanningStateModel>, { slot }: PlanningCreateSlotRequest) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => {
        let overlapingSlot: SlotOccurrence;
        if (!(overlapingSlot = this.checkSlotAvailable(ctx, slot))) {
          return this.planningService.addSlotToPlanning(id, slot);
        }
        return of(overlapingSlot);
      }),
      mergeMap((m: any) => {
        if (m && m.status) {
          // Slot not overriding, added to planning
          const slotWithId: Slot = { ...slot, id: -1 };
          ctx.setState(
            patch<PlanningStateModel>({
              slots: append([slotWithId]),
            }),
          );
          return ctx.dispatch(new PlanningCreateSlotSuccess());
        }

        return ctx.dispatch(
          new SlotOverrideExisting({
            date: m.startDate.format('L'),
            hour: m.startDate.format('LT'),
          }),
        );
      }),
      catchError(err => {
        return ctx.dispatch(new PlanningCreateSlotFailure(err));
      }),
    );
  }

  @Action(PlanningDropSlotRequest)
  dropSlotRequest(
    ctx: StateContext<PlanningStateModel>,
    { slot }: PlanningDropSlotRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.planningService.dropSlotToPlanning(id, slot)),
      mergeMap((m: any) => ctx.dispatch(new PlanningCreateSlotSuccess())),
      catchError(err => ctx.dispatch(new PlanningCreateSlotFailure(err))),
    );
  }

  @Action([
    PlanningCreateSlotSuccess,
    PlanningEditSlotSuccess,
    PlanningDeleteSlotSuccess,
    PlanningMoveSlotSuccess,
  ])
  createSlotSuccess(ctx: StateContext<PlanningStateModel>) {
    ctx.dispatch(new GetPlanningRequest(ctx.getState().currentDatesRange));
  }

  @Action(PlanningEditSlotRequest)
  editSlot(ctx: StateContext<PlanningStateModel>, { slot }: PlanningEditSlotRequest) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => {
        let overlapingSlot: SlotOccurrence;
        if (!(overlapingSlot = this.checkSlotAvailable(ctx, slot))) {
          return this.planningService.editSlot(id, slot);
        }
        return of(overlapingSlot);
      }),
      mergeMap((m: any) => {
        if (m && m.status) {
          return ctx.dispatch(new PlanningEditSlotSuccess());
        }
        return ctx.dispatch(
          new SlotOverrideExisting({
            date: m.startDate.format('L'),
            hour: m.startDate.format('LT'),
          }),
        );
      }),
      catchError(err => ctx.dispatch(new PlanningEditSlotFailure(err))),
    );
  }

  @Action(PlanningDeleteSlotRequest)
  deleteSlot(
    ctx: StateContext<PlanningStateModel>,
    { slotId, occurrenceId, deleteAll }: PlanningDeleteSlotRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => {
        return this.planningService.deleteSlotFromPlanning(
          id,
          slotId,
          !deleteAll ? occurrenceId : null,
        );
      }),
      mergeMap(() => {
        return ctx.dispatch(new PlanningDeleteSlotSuccess());
      }),
      catchError(err => ctx.dispatch(new PlanningDeleteSlotFailure(err))),
    );
  }

  @Action(PlanningMoveSlotRequest)
  moveSlot(
    ctx: StateContext<PlanningStateModel>,
    { slot, occurrence, moveAll }: PlanningMoveSlotRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => {
        return moveAll
          ? this.planningService.moveSlot(id, slot)
          : this.planningService.moveOccurrence(id, slot, occurrence);
      }),
      mergeMap(() => {
        return ctx.dispatch(new PlanningMoveSlotSuccess());
      }),
      catchError(err => ctx.dispatch(new PlanningMoveSlotFailure(err))),
    );
  }

  /**
   * Clear planning between each date change to avoid blink effect on events
   */
  @Action(ClearPlanning)
  clearPlanning(ctx: StateContext<PlanningStateModel>) {
    ctx.patchState({
      slots: [],
    });
  }

  /**
   * Check if the date is available for the current slot
   * @param ctx the context of the state
   * @param slot the slot we try to add/modify
   * @return the slot we overlap, or null if there is no slot
   */
  checkSlotAvailable(ctx: StateContext<PlanningStateModel>, slot: Slot): SlotOccurrence {
    const allExistingOccurrences: SlotOccurrence[] = PlanningState.allOccurrences(
      ctx.getState(),
    )(slot.id).filter(occ => occ.endDate.isAfter(slot.start));
    const slotAllOccurrences = PlanningUtilsService.slotToOccurrences(slot);

    let ok: SlotOccurrence = null;
    slotAllOccurrences.forEach(currentSlotOcc => {
      allExistingOccurrences.forEach(existingSlotsOcc => {
        if (
          (currentSlotOcc.startDate.isSameOrAfter(existingSlotsOcc.startDate) &&
            currentSlotOcc.startDate.isBefore(existingSlotsOcc.endDate)) ||
          (currentSlotOcc.endDate.isAfter(existingSlotsOcc.startDate) &&
            currentSlotOcc.endDate.isSameOrBefore(existingSlotsOcc.endDate)) ||
          (currentSlotOcc.startDate.isBefore(existingSlotsOcc.startDate) &&
            currentSlotOcc.endDate.isAfter(existingSlotsOcc.endDate))
        ) {
          ok = existingSlotsOcc;
        }
      });
    });

    return ok;
  }

  @Action(SetCurrentDay)
  setCurrentDay(ctx: StateContext<PlanningStateModel>, { currentDay }: SetCurrentDay) {
    const cloneCurrent = currentDay
      .clone()
      .tz(this.store.selectSnapshot(RadioState.currentRadioTimezone), true);
    const currentDatesRange: DatesRange = {
      from: cloneCurrent.clone().startOf('week'),
      to: cloneCurrent.clone().endOf('week').add(1, 'day'),
    };
    const prevRange = ctx.getState().currentDatesRange.from;
    ctx.patchState({ currentDay: cloneCurrent, currentDatesRange });
    if (prevRange && !prevRange.isSame(currentDatesRange.from)) {
      ctx.dispatch(new GetPlanningRequest(currentDatesRange));
    }
  }

  getCurrentRadioId(): Observable<number> {
    return this.store.select(RadioState.currentRadioId).pipe(
      filter(data => !!data),
      take(1),
    );
  }

  getCurrentRadioTimezone(): Observable<string> {
    return this.store.select(RadioState.currentRadioTimezone).pipe(
      filter(data => !!data),
      take(1),
    );
  }
}
